import { Injectable } from '@angular/core';

function getFileName(mimeType: string) {
  return `d${(+new Date()).toString(16)}${mimeType === 'application/pdf' ? '.pdf' : ''}`;
}

@Injectable()
export class NativeWindowService {
  constructor() {}

  get window(): Window {
    return window;
  }

  openFile(file: Blob, fileName?: string, tab?: any): void {
    if (!file) {
      throw new Error('Invalid operation. File is missing.');
    }

    if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
      if (!fileName || fileName.trim() === '') {
        fileName = getFileName(file.type);
      }
      (window.navigator as any).msSaveOrOpenBlob(file, fileName);
    } else {
      const url = URL.createObjectURL(file);
      if (!tab) {
        tab = this.openTab();
      }
      tab.location.assign(url);
    }
  }

  newTab(): Window {
    return window.open();
  }

  navigate(window: Window, url: string) {
    if (window) window.location.assign(url);
  }

  showLoading(tab: Window, message: string): void {
    this.showMessage(tab, message, false);
  }

  showError(tab: Window, message: string): void {
    this.showMessage(tab, message, true);
  }

  private showMessage(tab: Window, message: string, isError: boolean): void {
    if (!tab || tab.closed || !tab.document || !tab.document.body || !tab.document.head) return;

    const document = tab.document;
    document.title = isError ? 'Fastighetsägarna Dokument' : 'Laddar Fastighetsägarna Dokument';
    while (document.body.firstChild) document.body.removeChild(document.body.firstChild);

    const style = document.createElement('style');
    style.textContent = `
      @keyframes fad-spin { to { transform: rotate(360deg); } }
      * { box-sizing: border-box; }
      body {
        margin: 0;
        min-height: 100vh;
        background: #f4f7f8;
        color: #193b56;
        font-family: Arial, sans-serif;
      }
      .fad-shell {
        min-height: 100vh;
        display: flex;
        flex-direction: column;
      }
      .fad-header {
        height: 64px;
        display: flex;
        align-items: center;
        padding: 0 32px;
        background: #fff;
        border-bottom: 1px solid #d8e0e3;
      }
      .fad-logo {
        width: 28px;
        height: 28px;
        display: grid;
        place-items: center;
        margin-right: 12px;
        border: 2px solid #193b56;
        border-radius: 7px;
        background: #f6d34a;
        color: #193b56;
        font-size: 16px;
        font-weight: 700;
      }
      .fad-brand { font-size: 18px; font-weight: 600; letter-spacing: .01em; }
      .fad-content {
        width: min(100% - 32px, 520px);
        margin: auto;
        padding: 24px 0;
      }
      .fad-card {
        position: relative;
        overflow: hidden;
        padding: 40px 36px 36px;
        border: 1px solid #d8e0e3;
        border-radius: 12px;
        background: #fff;
        box-shadow: 0 8px 24px rgba(25, 59, 86, .08);
        text-align: center;
      }
      .fad-card::before {
        content: '';
        position: absolute;
        inset: 0 0 auto;
        height: 5px;
        background: #27a99d;
      }
      .fad-icon {
        width: 48px;
        height: 48px;
        display: inline-grid;
        place-items: center;
        margin-bottom: 20px;
        border-radius: 50%;
        background: #e3f5f3;
        color: #168f86;
        font-size: 25px;
        font-weight: 700;
      }
      .fad-spinner {
        width: 24px;
        height: 24px;
        border: 3px solid #b9e5e1;
        border-top-color: #168f86;
        border-radius: 50%;
        animation: fad-spin .8s linear infinite;
      }
      h1 { margin: 0 0 10px; font-size: 22px; font-weight: 600; }
      .fad-message { margin: 0; color: #475467; font-size: 16px; line-height: 1.5; }
      .fad-hint { margin: 18px 0 0; color: #667085; font-size: 13px; }
      .fad-error .fad-icon { background: #fef0ef; color: #b42318; }
      .fad-card.fad-error::before { background: #d92d20; }
    `;
    document.head.appendChild(style);

    const shell = document.createElement('div');
    shell.className = 'fad-shell';

    const header = document.createElement('header');
    header.className = 'fad-header';
    const logo = document.createElement('span');
    logo.className = 'fad-logo';
    logo.textContent = 'H';
    const brand = document.createElement('span');
    brand.className = 'fad-brand';
    brand.textContent = 'DinHyresvärd';
    header.append(logo, brand);

    const content = document.createElement('main');
    content.className = 'fad-content';
    const card = document.createElement('section');
    card.className = `fad-card${isError ? ' fad-error' : ''}`;
    const icon = document.createElement('div');
    icon.className = 'fad-icon';
    if (isError) {
      icon.textContent = '!';
    } else {
      const spinner = document.createElement('span');
      spinner.className = 'fad-spinner';
      icon.appendChild(spinner);
    }
    const heading = document.createElement('h1');
    heading.textContent = 'Fastighetsägarna Dokument';
    const paragraph = document.createElement('p');
    paragraph.className = 'fad-message';
    paragraph.textContent = message;
    const hint = document.createElement('p');
    hint.className = 'fad-hint';
    hint.textContent = isError
      ? 'Stäng fliken och försök igen från kontraktet.'
      : 'Du kan stanna kvar på sidan medan anslutningen upprättas.';
    card.append(icon, heading, paragraph, hint);
    content.appendChild(card);
    shell.append(header, content);
    document.body.appendChild(shell);
  }

  openTab(url?: string) {
    const newTab = this.newTab();
    if (!url) return newTab;

    this.navigate(newTab, url);
  }

  saveWithDecodedFileName(file: File) {
    let result = new File([file], decodeURIComponent(file.name.replace(/\+/g, ' ')));
    this.save(result);
  }

  save(file: File) {
    if (!file) {
      return;
    }

    if (window.document) {
      const element = window.document.createElement('a') as HTMLAnchorElement;
      if (element) {
        window.document.body.appendChild(element);
        element.download = file.name;
        element.href = URL.createObjectURL(file);
        element.click();
        window.document.body.removeChild(element);
        URL.revokeObjectURL(element.href);
      }
    }
  }
}
